/* eslint-disable react/jsx-curly-newline */
/* eslint-disable implicit-arrow-linebreak */
/* eslint-disable */
import React, { useEffect, useState } from 'react';

import Tippy from '@tippyjs/react';
import { useQuery } from '@tanstack/react-query';

import { IPayHistory } from 'src/interfaces';
import { TooltipBoxTable, Loader } from 'pg-ui-library';
import { useTypedSelector } from 'src/hooks/useTypedSelector';
import PaymentsService from 'src/services/payments.service';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';
import { CellSpan } from './styles';

const CustomCell: React.FC<{ value: string; children: React.ReactNode }> = ({ value, children }) => (
  <div>
    <Tippy placement="bottom" content={children}>
      <CellSpan title={value}>{value}</CellSpan>
    </Tippy>
  </div>
);

const columns = [
  {
    name: <div>Pay Statement Number</div>,
    selector: (row: IPayHistory) => row.payStatementNumber,
    cell: ({ payStatementNumber }: IPayHistory) => (
      <CellSpan title={payStatementNumber} data-tag="allowRowEvents">
        {payStatementNumber}
      </CellSpan>
    ),
    wrap: true,
  },
  {
    name: <div>Check Date</div>,
    selector: (row: IPayHistory) => row.checkDate,
    wrap: true,
  },
  {
    name: <div>Employee ID</div>,
    selector: (row: IPayHistory) => row.personnelNumber,
    wrap: true,
  },
  {
    name: <div>Name</div>,
    selector: (row: IPayHistory) => row.employeeName,
    wrap: true,
  },
  {
    name: <div>Department</div>,
    selector: (row: IPayHistory) => row.department,
    wrap: true,
  },
  {
    name: <div>Position</div>,
    selector: (row: IPayHistory) => row.position,
    wrap: true,
  },
  {
    name: <div>Location</div>,
    selector: (row: IPayHistory) => row.location,
    wrap: true,
  },
  {
    name: <div>Hours</div>,
    selector: (row: IPayHistory) => row.employeeTaxes.value,
    cell: ({ hours }: IPayHistory) => (
      <CustomCell value={hours.value}>
        <TooltipBoxTable title="Hours" columns={hours.columns} rows={hours.rows} />
      </CustomCell>
    ),
    wrap: true,
  },
  {
    name: <div>Employee Taxes</div>,
    selector: (row: IPayHistory) => row.employeeTaxes.value,
    cell: ({ employeeTaxes }: IPayHistory) => (
      <CustomCell value={employeeTaxes.value}>
        <TooltipBoxTable title="Employee Taxes" columns={employeeTaxes.columns} rows={employeeTaxes.rows} />
      </CustomCell>
    ),
    wrap: true,
  },
  {
    name: <div>Gross Pay</div>,
    selector: (row: IPayHistory) => row.grossPay,
  },
  {
    name: <div>Total Deductions</div>,
    selector: (row: IPayHistory) => row.totalDeductions.value,
    cell: ({ totalDeductions }: IPayHistory) => (
      <CustomCell value={totalDeductions.value}>
        <TooltipBoxTable title="Total Deductions" columns={totalDeductions.columns} rows={totalDeductions.rows} />
      </CustomCell>
    ),
    wrap: true,
  },
  {
    name: <div>Total Benefits</div>,
    selector: (row: IPayHistory) => row.totalBenefits.value,
    cell: ({ totalBenefits }: IPayHistory) => (
      <CustomCell value={totalBenefits.value}>
        <TooltipBoxTable title="Total Benefits" columns={totalBenefits.columns} rows={totalBenefits.rows} />
      </CustomCell>
    ),
    wrap: true,
  },
  {
    name: <div>Net Pay</div>,
    selector: (row: IPayHistory) => row.netPay,
    wrap: true,
  },
  {
    name: <div>Employer Taxes</div>,
    selector: (row: IPayHistory) => row.employerTaxes.value,
    cell: ({ employerTaxes }: IPayHistory) => (
      <CustomCell value={employerTaxes.value}>
        <TooltipBoxTable title="Employer Taxes" columns={employerTaxes.columns} rows={employerTaxes.rows} />
      </CustomCell>
    ),
    wrap: true,
  },
  {
    name: <div>Payment Method</div>,
    selector: (row: IPayHistory) => row.paymentMethod,
    wrap: true,
  },
  {
    name: 'Link',
    button: true,
    cell: (row: IPayHistory) => (
      <a
        href={PaymentsService.getPayStubPdf({
          ptn: row.payStatementNumber || '',
          companyId: useTypedSelector(state => state.config.data?.config?.selectedCompanyId) || '',
          employeeId: row.employeeId,
          downloadFile: true,
        })}
        target="_blank"
        rel="noopener noreferrer"
      >
        Download
      </a>
    ),
  },
];

const customStyles = {
  headRow: {
    style: {
      border: 'none',
    },
  },
  headCells: {
    style: {
      color: '#9399B2',
      font: 'normal normal bold 15px Biotif',
    },
  },
  rows: {
    style: {
      font: 'normal normal normal 14px Biotif',
      color: '#1B1D35',
      borderRadius: '5px',
      '&:not(:last-of-type)': {
        borderBottomWidth: '0px',
      },
    },
    stripedStyle: {
      color: '#1B1D35',
      backgroundColor: '#F3F4F8',
    },
    highlightOnHoverStyle: {
      backgroundColor: 'rgb(230, 244, 244)',
      borderBottomColor: '#FFFFFF',
      borderRadius: '25px',
      outline: '1px solid #FFFFFF',
    },
  },
  cells: {
    style: {
      fontWeight: 'bold',
      justifyContent: 'left',
    },
  },
  pagination: {
    style: {
      border: 'none',
      svg: {
        fill: 'rgba(0,0,0,0.54)',
      },
    },
  },
};

const PayHistoryTable: React.FC<{
  searchTerm: string;
  startDate: string;
  endDate: string;
}> = ({ searchTerm, startDate, endDate }) => {
  const employeeReviewTableColumns: GridColDef[] = [
    {
      headerName: 'Pay Statement Number',
      headerClassName: 'CommonHeaderClass',
      field: 'payStatementNumber',
      renderCell: params => (
        <CellSpan title={params.value} data-tag="allowRowEvents">
          {params.value}
        </CellSpan>
      ),
    },
    {
      headerName: 'Check Date',
      headerClassName: 'CommonHeaderClass',
      field: 'checkDate',
      sortable: true,
      filterable: true,
    },
    {
      headerName: 'Employee ID',
      headerClassName: 'CommonHeaderClass',
      field: 'personnelNumber',
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Name',
      headerClassName: 'CommonHeaderClass',
      field: 'employeeName',
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Department',
      headerClassName: 'CommonHeaderClass',
      field: 'department',
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Position',
      headerClassName: 'CommonHeaderClass',
      field: 'position',
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Location',
      headerClassName: 'CommonHeaderClass',
      field: 'location',
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Hours',
      headerClassName: 'CommonHeaderClass',
      field: 'hours',
      sortable: true,
      filterable: false,
      valueGetter: (params: GridCellParams) => params.value,
      renderCell: (params: GridCellParams) => {
        const hours = params.row.hours as { value: string; columns: any[]; rows: any[] };
        const displayValue = typeof hours === 'string' ? hours : hours.value;
        const columns = hours && hours.columns ? hours.columns : [];
        const rows = hours && hours.rows ? hours.rows : [];
        return (
          <CustomCell value={displayValue}>
            <TooltipBoxTable title="Hours" columns={columns} rows={rows} />
          </CustomCell>
        );
      },
    },
    {
      headerName: 'Employee Taxes',
      headerClassName: 'CommonHeaderClass',
      field: 'employeeTaxes',
      valueGetter: (params: GridCellParams) => params.value,
      renderCell: (params: GridCellParams) => (
        <CustomCell value={String(params.value)}>
          <TooltipBoxTable
            title="Employee Taxes"
            columns={params.row.employeeTaxes.columns}
            rows={params.row.employeeTaxes.rows}
          />
        </CustomCell>
      ),
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Gross Pay',
      headerClassName: 'CommonHeaderClass',
      field: 'grossPay',
      sortable: true,
    },
    {
      headerName: 'Total Deductions',
      headerClassName: 'CommonHeaderClass',
      field: 'totalDeductions',
      valueGetter: (params: GridCellParams) => params.value,
      renderCell: (params: GridCellParams) => (
        <CustomCell value={String(params.value)}>
          <TooltipBoxTable
            title="Total Deductions"
            columns={params.row.totalDeductions.columns}
            rows={params.row.totalDeductions.rows}
          />
        </CustomCell>
      ),
      sortable: true,
      filterable: false,
    },
    {
      headerName: 'Total Benefits',
      headerClassName: 'CommonHeaderClass',
      field: 'totalBenefits',
      valueGetter: (params: GridCellParams) => params.value,
      renderCell: (params: GridCellParams) => (
        <CustomCell value={String(params.value)}>
          <TooltipBoxTable
            title="Total Benefits"
            columns={params.row.totalBenefits.columns}
            rows={params.row.totalBenefits.rows}
          />
        </CustomCell>
      ),
      sortable: true,
    },
    {
      headerName: 'Net Pay',
      headerClassName: 'CommonHeaderClass',
      field: 'netPay',
      sortable: true,
    },
    {
      headerName: 'Employer Taxes',
      headerClassName: 'CommonHeaderClass',
      field: 'employerTaxes',
      valueGetter: (params: GridCellParams) => params.value,
      renderCell: (params: GridCellParams) => (
        <CustomCell value={String(params.value)}>
          <TooltipBoxTable
            title="Employer Taxes"
            columns={params.row.employerTaxes.columns}
            rows={params.row.employerTaxes.rows}
          />
        </CustomCell>
      ),
      sortable: true,
    },
    {
      headerName: 'Payment Method',
      headerClassName: 'CommonHeaderClass',
      field: 'paymentMethod',
      sortable: true,
    },
    {
      headerName: 'Link',
      headerClassName: 'CommonHeaderClass',
      field: '',
      renderCell: params => (
        <a
          href={PaymentsService.getPayStubPdf({
            ptn: params.row.payStatementNumber || '',
            companyId: useTypedSelector(state => state.config.data?.config?.selectedCompanyId) || '',
            employeeId: params.row.employeeId,
            downloadFile: true,
          })}
          target="_blank"
          rel="noopener noreferrer"
        >
          Download
        </a>
      ),
    },
  ];

  const { config } = useTypedSelector(state => state.config.data);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [sortBy, setSortBy] = useState('');
  const [sortDirection, setSortDirection] = useState('');

  const { isFetching, data, refetch } = useQuery(['company-pay-history'], async () => {
    const result = await PaymentsService.getCompanyPayHistory({
      page,
      companyId: config?.selectedCompanyId || '',
      limit,
      startDate,
      endDate,
      search: searchTerm,
      sortBy,
      sortDirection,
    });
    return result;
  });

  useEffect(() => {
    refetch();
  }, [searchTerm, sortBy, sortDirection]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePerRowsChange = async (newLimit: number, newPage: number) => {
    setLimit(newLimit);
    setPage(newPage);
  };

  useEffect(() => {
    if (config?.selectedCompanyId) {
      refetch();
    }
  }, [page, limit, startDate, endDate, config?.selectedCompanyId]);

  const onPageMetaChange = async (changes: any) => {
    const persentPage = changes.page + 1;
    if (persentPage > page) {
      handlePageChange(page + 1);
    }
    if (persentPage < page) {
      handlePageChange(page - 1);
    }
    if (changes.pageSize !== limit) {
      handlePerRowsChange(changes.pageSize, page);
    }
  };

  const onSortMetaChange = async (changes: any) => {
    setSortBy(changes[0].field as unknown as string);
    setSortDirection(changes[0].sort);
  };

  if (!config?.selectedCompanyId) {
    return <Loader />;
  }
  const formattedData = data?.data.items?.map((row, index) => ({
    ...row,
    id: row.id || index + 1,
  }));

  return (
    <div style={{ width: '100%', boxShadow: '0px 0px 6px #E3E3E3', borderRadius: '5px' }}>
      {formattedData && formattedData.length > 0 ? (
        <DataGrid
          rows={formattedData}
          columns={employeeReviewTableColumns}
          pagination
          paginationMode="server"
          onPaginationModelChange={state => {
            onPageMetaChange(state);
          }}
          onSortModelChange={state => {
            onSortMetaChange(state);
          }}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 15 },
            },
          }}
          pageSizeOptions={[15, 25, 50]}
          rowCount={data?.data.meta.totalItems}
          loading={isFetching}
          disableColumnFilter
          sx={{
            font: '14px Biotif',
            fontWeight: 'bold',
            '& .CommonHeaderClass': {
              color: '#9399B2',
              font: '16px Biotif',
              fontWeight: 'bold',
            },
            minHeight: '150px',
          }}
        />
      ) : (
        <div style={{ minHeight: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          No payslips to display.
        </div>
      )}
    </div>
  );
};

export default PayHistoryTable;
